import inPercy from '@percy-io/in-percy';
import Highcharts, { AnnotationsOptions, PlotSeriesOptions } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import HighchartsExportData from 'highcharts/modules/export-data';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsAnnotations from 'highcharts/modules/annotations';
import { FC, useEffect, useRef, useState } from 'react';
import ReactResizeDetector from 'react-resize-detector';
import styled from 'styled-components';
import moment from 'moment-timezone';
import { rgba } from '../../utils/polished';
import useTheme from '../../utils/useTheme';
import { Theme } from '../theme';
import { ButtonTheme } from '../Button/Button.styles';

// Allows Highcharts to use Timezones
// https://github.com/highcharts/highcharts/issues/8661
if (typeof window !== `undefined`) {
  (window as any).moment = moment;
}

/** Setup additional Highcharts modules */
if (typeof Highcharts === 'object') {
  HighchartsAccessibility(Highcharts);
  HighchartsMore(Highcharts);
  HighchartsExporting(Highcharts);
  HighchartsExportData(Highcharts);
  HighchartsOfflineExporting(Highcharts);
  HighchartsAnnotations(Highcharts);

  // Temporary fix to resolve console errors when using the annotations module.
  // https://github.com/highcharts/highcharts/issues/12979

  // eslint-disable-next-line
  // @ts-ignore
  Highcharts.Annotation.prototype.getClipBox = function() {
    return {
      x: (this as any).clipXAxis.left || 0,
      y: (this as any).clipYAxis.top || 0,
      width: (this as any).clipXAxis.width || 0,
      height: (this as any).clipYAxis.height || 0,
    };
  };
}

const GraphContainer = styled.div`
  > div:first-child {
    overflow: visible !important;
  }
`;

const getGraphColor = (
  variation: ITimeSeriesHighGraphSeries['variation'],
  lineVariation: ITimeSeriesHighGraphSeries['lineVariation'],
  theme: Theme,
): string => {
  if (variation === 'secondary' && lineVariation === 'dashed') {
    return theme.vars.newColor.purple1;
  } else if (variation === 'secondary') {
    return theme.vars.newColor.purple4;
  } else if (lineVariation === 'dashed') {
    return theme.vars.newColor.blue1;
  }
  return theme.vars.newColor.blue2;
};

const getTooltip = (theme: Theme) => {
  return `<span style="display: flex; margin-bottom: 5px;">
          <svg height="30" width="15" style="filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.25))">
            <circle cx="7.5" cy="7.5" r="5" stroke="{point.color}" stroke-width="3" fill="${theme.vars.newColor.white}"  />
          </svg>
          <span style="margin-left: 8px">
            {point.tooltipLabel}
            <br />
            <span style="color: ${theme.vars.newColor.blue2}; font-size: 14px; margin-top: 5px; display: inline-block; font-weight: bold">{point.tooltipValue}</span>
          </span>
        </span>`;
};

export interface ITimeSeriesHighGraphSeries {
  /**
   * The name of the series. Currently only used in the tooltip.
   * Defaults to undefined.
   */
  name?: string;
  /** An array of numbers or x/y object representing all the data points in the series. */
  data: any[];
  /**
   * Sets the initial visibility of the series. Defaults to true.
   */
  visible?: boolean;
  /**
   * The name of the line variation to use for the series.  Can be either
   * "solid" or "dashed". Defaults to "solid".
   */
  lineVariation?: 'solid' | 'dashed';
  /**
   * One of "primary" : "secondary" where primary is the standard blue design
   * and secondary is the pink alternative. Defaults to "primary".
   */
  variation?: 'primary' | 'secondary' | 'overlay';
  /** Whether to fill the area below the line or not.  Defaults to true. */
  areaFill?: boolean;
  /** Series opacity 0 - 1. */
  lineOpacity?: number;
  /** Series type: passed through to HighCharts */
  type?: 'area' | 'areasplinerange';
  /** Series z-index: passed through to HighCharts */
  zIndex?: number;
}

export interface IProps {
  /** Heading text that appears above the chart. */
  heading: string;
  /** Title text for the xAxis (Only appears on the export). */
  xAxisTitle: string;
  /** Title text for the yAxis (Only appears on the export). */
  yAxisTitle: string;
  /** Array of series data - see ITimeSeriesHighGraphSeries for more details. */
  series: ITimeSeriesHighGraphSeries[];
  /** The text of the tooltip heading. */
  tooltipHeading: string;
  /** The text of the tooltip sub-heading. */
  tooltipSubHeading?: string;
  /**
   * Select whether or not the charts axis should be shown in UTC or if it
   * should use local time. Defaults to `true`.
   */
  useUTC?: boolean;
  /**
   * Enter a timezone string (e.g. America/_York) to display
   * the chart in that specific timezone. If no timezone provided the chart will
   * be displayed in UTC. Defaults to `Europe/London`
   */
  timezone?: string;
  /**
   * Select whether or not the charts series should stack on top of each other.
   * Defaults to undefined.  Other options are 'normal' and 'percent'.
   */
  stacking?: PlotSeriesOptions['stacking'];
  /** This will be automatically calculated by the `TimeSeriesHighPanel`. */
  chartHeight?: number;
  /** When enabled it will show the annotation options in the menu. */
  enableAnnotations?: boolean;
  /** Function to run when clicking on an annotation. */
  annotationOnClick?: (annotationEvent: any) => void;
  /** Annotations to show on the chart */
  annotations?: Pick<AnnotationsOptions, 'id' | 'labels' | 'labelOptions'>[];
  /** Function to run when clicking on the `Add note` button on the export menu. */
  addNoteOnClick?: () => void;
  /** Text for the `addNote` button on the export menu. Default: `Add note` */
  addNoteText?: string;
  /** Function to run when clicking on the `View all notes` button on the export menu. */
  viewAllNotesOnClick?: () => void;
  /** Text for the `viewAllNotes` button on the export menu. Default: `View all notes` */
  viewAllNotesText?: string;
}

export const TimeSeriesHighGraph: FC<IProps> = ({
  addNoteOnClick,
  addNoteText = 'Add a note',
  annotationOnClick,
  annotations,
  chartHeight,
  enableAnnotations,
  heading,
  series,
  stacking,
  timezone = 'Europe/London',
  tooltipHeading,
  tooltipSubHeading,
  useUTC = true,
  viewAllNotesOnClick,
  viewAllNotesText = 'View all notes',
  xAxisTitle,
  yAxisTitle,
}) => {
  const theme = useTheme();
  const zoomButtonTheme = ButtonTheme(theme)['secondary'];

  const [chart, setChart] = useState<any>(null);
  const graphContainer = useRef(null);
  const overlayColor: Highcharts.ColorString = rgba(theme.vars.newColor.green4, 0.6);

  useEffect(() => {
    Highcharts.setOptions({
      lang: {
        resetZoomTitle: 'Reset zoom',
      },
    });
  }, []);

  const stringifiedSeries = JSON.stringify(series);
  useEffect(() => {
    /**
     * If the series data changes we need to zoom the chart back out to prevent
     * any rendering issues with the axis.
     */
    if (chart && chart.hasRendered && typeof chart.exporting === 'object') {
      chart.zoomOut();
    }
  }, [chart, stringifiedSeries]);

  const reflowChart = () => {
    /**
     * Reflows the chart to its container. By default the chart will
     * automatically reflow following a window.resize but we need to explicitly
     * call it if it's container resizes.
     */
    if (chart && chart.hasRendered && typeof chart.exporting === 'object') {
      chart.reflow();
    }
  };

  const afterChartCreated = (chartObject: any) => {
    if (chartObject) {
      setChart(chartObject);
    }
  };

  const getAnnotationDefaults = (isExport = false): Partial<AnnotationsOptions> => ({
    draggable: '',
    events: {
      // eslint-disable-next-line
      // @ts-ignore
      click: function() {
        annotationOnClick(this);
      },
    },
    labelOptions: {
      backgroundColor: theme.vars.newColor.white,
      borderColor: theme.vars.newColor.black,
      borderRadius: 0,
      borderWidth: isExport ? 1 : 2,
      distance: 10,
      padding: isExport ? 3 : 6,
      shadow: {
        color: theme.vars.newColor.black,
        offsetX: 1,
        offsetY: 1,
        opacity: 0.25,
      },
      style: {
        color: 'contrast',
        fontSize: isExport ? '6px' : '12px',
        fontWeight: '500',
        textOverflow: 'ellipsis',
        width: 125,
      },
    },
  });

  const getAnnotations = (isExport = false): AnnotationsOptions[] => {
    if (!chart || !chart.hasRendered || !enableAnnotations || !annotations || !annotationOnClick) {
      return null;
    }

    const annotationOptions: AnnotationsOptions[] = [];

    annotations.forEach(annotationsData => {
      if (annotationsData.labels) {
        annotationOptions.push({
          ...getAnnotationDefaults(isExport),
          ...annotationsData,
          labelOptions: {
            ...getAnnotationDefaults(isExport).labelOptions,
            ...annotationsData.labelOptions,
          },
        });
      }
    });

    return annotationOptions;
  };

  const getOptions = (chartHeight: number) => {
    let menuItems: string[] = [];

    const defaultMenuItems = [
      'downloadPNG',
      'downloadJPEG',
      'downloadPDF',
      'downloadSVG',
      'separator',
      'downloadCSV',
      'downloadXLS',
    ];

    const annotationMenuItems = ['addNote', 'viewAllNotes', 'separator'];

    if (enableAnnotations && addNoteOnClick && viewAllNotesOnClick) {
      menuItems = [...annotationMenuItems, ...defaultMenuItems];
    } else {
      menuItems = [...defaultMenuItems];
    }

    const options: Highcharts.Options = {
      annotations: getAnnotations(),
      accessibility: {
        announceNewData: {
          enabled: true,
        },
        enabled: true,
        keyboardNavigation: {
          seriesNavigation: {
            mode: 'serialize',
          },
        },
      },
      chart: {
        type: 'area',
        height: chartHeight >= 200 ? chartHeight : 200,
        style: {
          fontFamily: 'Whitney, sans-serif',
          overflow: 'visible',
        },
        spacingLeft: 0,
        spacingTop: 0,
        spacingBottom: 0,
        animation: inPercy() ? false : true,
        zoomType: 'x',
        resetZoomButton: {
          position: {
            x: 0,
            y: -10,
          },
          theme: {
            fill: zoomButtonTheme.bgColor,
            stroke: zoomButtonTheme.borderColor,
            r: 0,
            style: {
              color: zoomButtonTheme.color,
              fontFamily: 'Whitney, sans-serif',
              fontWeight: '600',
            },
            states: {
              hover: {
                fill: zoomButtonTheme.hoverBgColor,
                stroke: zoomButtonTheme.hoverBorderColor,
              },
            },
          },
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        accessibility: {
          // eslint-disable-next-line
          // @ts-ignore
          enabled: true,
        },
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            menuItems,
            symbol: 'menuball',
            symbolFill: theme.vars.newColor.darkGS2,
            symbolSize: 10,
            symbolStroke: theme.vars.newColor.darkGS2,
            symbolStrokeWidth: 3,
            width: 18,
            x: -5,
            y: -33,
          },
        },
        menuItemDefinitions: {
          addNote: {
            onclick: function() {
              addNoteOnClick();
            },
            text: addNoteText,
          },
          viewAllNotes: {
            onclick: function() {
              viewAllNotesOnClick();
            },
            text: viewAllNotesText,
          },
        },
        chartOptions: {
          annotations: getAnnotations(true),
          legend: {
            enabled: true,
          },
          plotOptions: {
            series: {
              dataLabels: {
                enabled: true,
              },
              marker: {
                enabled: false,
              },
            },
            areasplinerange: {
              dataLabels: {
                enabled: false,
              },
            },
          },
          title: {
            style: {
              display: 'block',
            },
          },
        },
      },
      legend: {
        enabled: false,
        itemStyle: {
          fontSize: '10px',
        },
      },
      navigation: {
        menuItemHoverStyle: {
          color: theme.vars.newColor.darkGS1,
          backgroundColor: theme.vars.newColor.blue4,
        },
        menuItemStyle: {
          color: theme.vars.newColor.darkGS1,
          fontSize: '14px',
        },
        menuStyle: {
          border: `1px solid ${theme.vars.newColor.lightGS1}`,
          borderRadius: 4,
        },
      },
      plotOptions: {
        series: {
          animation: inPercy() ? false : true,
          stacking: stacking,
          turboThreshold: 2000,
          dataLabels: {
            enabled: false,
            style: {
              fontSize: '6px',
              fontWeight: 'normal',
            },
          },
        },
        areasplinerange: {
          animation: inPercy() ? false : true,
          marker: {
            enabled: false,
          },
        },
      },
      series: series.map(series => {
        const { areaFill, data, lineVariation, name, variation, visible = true, zIndex } = series;
        const graphColor = getGraphColor(variation, lineVariation, theme);

        if (series.type === 'areasplinerange') {
          const areaSplineRangeSeries: Highcharts.SeriesAreasplinerangeOptions = {
            color: theme.vars.newColor.green4,
            dashStyle: lineVariation === 'dashed' ? 'Dot' : 'Solid',
            data,
            fillColor: !areaFill
              ? 'transparent'
              : variation === 'secondary'
              ? theme.vars.newColor.purple4
              : variation === 'overlay'
              ? overlayColor
              : theme.vars.newColor.blue2,
            lineColor: 'transparent',
            lineWidth: 0,
            marker: { enabled: false },
            name,
            shadow: false,
            type: 'areasplinerange',
            visible,
            zIndex,
          };

          return areaSplineRangeSeries;
        }

        const areaSeriesOptions: Highcharts.SeriesAreaOptions = {
          color: graphColor,
          dashStyle: lineVariation === 'dashed' ? 'Dot' : 'Solid',
          data,
          fillColor: !areaFill ? 'transparent' : graphColor,
          lineWidth: lineVariation === 'dashed' ? 1 : 2,
          marker: {
            symbol: 'circle',
            lineColor: graphColor,
            fillColor: theme.vars.newColor.white,
            lineWidth: 3,
            radius: 5,
          },
          name,
          shadow: false,
          type: 'area',
          visible,
          zIndex,
        };

        return areaSeriesOptions;
      }),
      time: { useUTC: useUTC, timezone: timezone },
      title: {
        text: heading ? heading : null,
        style: {
          display: 'none',
          fontSize: '14px',
        },
      },
      tooltip: {
        backgroundColor: theme.vars.newColor.white,
        borderColor: theme.vars.newColor.lightGS2,
        borderRadius: 0,
        borderWidth: 2,
        distance: 30,
        shared: true,
        useHTML: true,
        headerFormat: `<b style='display: inline-block; margin-bottom: 5px;'>${tooltipHeading}</b>${
          tooltipSubHeading
            ? '<br /><span style="color: ' + theme.vars.newColor.darkGS3 + '">' + tooltipSubHeading + '</span>'
            : ''
        }<br/><br/>`,
        pointFormat: getTooltip(theme),
      },
      xAxis: {
        accessibility: {
          enabled: true,
        },
        type: 'datetime',
        crosshair: { width: 1, color: theme.vars.newColor.blue2 },
        title: {
          reserveSpace: false,
          text: xAxisTitle ? xAxisTitle : null,
          style: {
            display: 'none',
          },
        },
        lineColor: 'transparent',
        tickColor: 'transparent',
        labels: {
          style: {
            color: theme.vars.newColor.darkGS2,
            fontSize: '12px',
          },
        },
      },
      yAxis: {
        accessibility: {
          enabled: true,
        },
        opposite: true,
        gridLineColor: theme.vars.newColor.lightGS2,
        title: {
          reserveSpace: false,
          text: yAxisTitle ? yAxisTitle : null,
          style: {
            display: 'none',
          },
        },
        labels: {
          style: {
            color: theme.vars.newColor.darkGS2,
            fontSize: '12px',
          },
        },
      },
    };

    return options;
  };

  return (
    <GraphContainer ref={graphContainer}>
      {chartHeight !== 0 && (
        <ReactResizeDetector handleWidth={true} onResize={reflowChart} refreshMode="throttle" refreshRate={150}>
          <HighchartsReact highcharts={Highcharts} options={getOptions(chartHeight)} callback={afterChartCreated} />
        </ReactResizeDetector>
      )}
    </GraphContainer>
  );
};

export default TimeSeriesHighGraph;
